import { createSelector, createSlice } from "@reduxjs/toolkit";
import { selectAlertsTexts } from "../lang/langSlice";
import { generateUUID } from "../../common/utils";

export const handleApiError = (error, onFieldsError) => {
  return async (dispatch) => {
    const duration = 15000;
    const durationSuggestions = 30000;

    console.error(error?.response);

    try {
      const status = error.response.status;
      if (status === 409 || status === "canceled") {
        // 409 est géré par l'intercepteur
        // canceled = annulation de requête par l'utilisateur
        return;
      }

      const data = error.response.data;
      const { code, errors, suggestions } = data;

      const showSuggestions = () => {
        if (!suggestions) return;
        try {
          if (Array.isArray(suggestions)) {
            suggestions.forEach((s) =>
              dispatch(addAlert(s, "info", durationSuggestions))
            );
          } else {
            dispatch(addAlert(suggestions, "info", durationSuggestions));
          }
        } catch {
          //
        }
      };

      if (!errors) {
        dispatch(addDefaulErrorAlert());
        showSuggestions();
        return;
      }

      switch (code) {
        case "fields_error": {
          onFieldsError?.(errors); // permet au composant de mettre à jour le state d'erreurs

          for (const property in errors) {
            const fieldErrors = errors[property] || [];
            fieldErrors.forEach((d) =>
              dispatch(addAlert(d, "error", duration))
            );
          }

          showSuggestions();

          break;
        }

        default: {
          if (Array.isArray(errors)) {
            errors.forEach((d) => dispatch(addAlert(d, "error", duration)));
          } else {
            dispatch(addAlert(errors, "error", duration));
          }

          showSuggestions();

          break;
        }
      }
    } catch (err) {
      console.error("Error in handleApiError: ", err);
      dispatch(addDefaulErrorAlert());
    }
  };
};

export const addAlert = (text, level, duration = 7500) => {
  return async (dispatch) => {
    const id = generateUUID();
    dispatch(
      alertsActions.add({
        id,
        text,
        level,
        duration,
        open: true,
      })
    );
  };
};

export const addDefaulErrorAlert = () => {
  return async (dispatch, getState) => {
    const { defaultErrorText } = selectAlertsTexts(getState());
    dispatch(addAlert(defaultErrorText, "error"));
  };
};

export const addDefaultSuccessAlert = () => {
  return async (dispatch, getState) => {
    const { defaultSuccessText } = selectAlertsTexts(getState());
    dispatch(addAlert(defaultSuccessText, "success"));
  };
};

const initialState = {
  items: [],
  actions: {},
};

const alertsSlice = createSlice({
  name: "alerts",
  initialState,
  reducers: {
    add: (state, action) => {
      state.items.push(action.payload);
    },
    close: (state, action) => {
      state.items = state.items.map((d) =>
        d.id === action.payload ? { ...d, open: false } : d
      );
    },
    clear: (state, action) => {
      state.items = state.items.filter((d) => d.id !== action.payload);
    },
    setActions: (state, action) => {
      state.actions = action.payload;
    },
    clearActions: (state) => {
      state.actions = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase("auth/logout", (state) => ({
      items: state.items,
      actions: {},
    }));
  },
});

export const alertsActions = alertsSlice.actions;

export const selectAlerts = (state) => state.alerts.items;

export const selectAlertsActions = createSelector(
  (state) => state.alerts.actions,
  (actions) => {
    const message = actions?.message || "";
    const buttons = actions?.buttons || [];
    const open = !!message && buttons.length > 0;
    return {
      message,
      buttons,
      open,
    };
  }
);

export default alertsSlice.reducer;
